import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getClipUrl } from '@/lib/api';
import { playTone, downloadHighlight } from '@/lib/audio';
import { themeColor, resolveHighlightAudioSrc } from '@/data/samples';

const PlaybackCtx = createContext(null);

function formatClock(sec) {
  const total = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(total / 60).toString().padStart(2, '0');
  const s = (total % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export function PlaybackProvider({ children }) {
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const toneTimerRef = useRef(null);
  const [highlightId, setHighlightId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [mode, setMode] = useState(null); // 'clip' | 'tone'

  const clearRaf = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const clearToneTimer = () => {
    if (toneTimerRef.current) {
      clearTimeout(toneTimerRef.current);
      toneTimerRef.current = null;
    }
  };

  const tick = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTime(audio.currentTime || 0);
    if (!Number.isNaN(audio.duration) && audio.duration) {
      setDuration(audio.duration);
    }
    if (!audio.paused && !audio.ended) {
      rafRef.current = requestAnimationFrame(tick);
    }
  }, []);

  const stop = useCallback(() => {
    clearRaf();
    clearToneTimer();
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.onended = null;
      audio.onerror = null;
      audio.ontimeupdate = null;
      audioRef.current = null;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setHighlightId(null);
    setMode(null);
  }, []);

  const pause = useCallback(() => {
    clearRaf();
    clearToneTimer();
    const audio = audioRef.current;
    if (audio && !audio.paused) audio.pause();
    setIsPlaying(false);
  }, []);

  const playToneFallback = useCallback((highlight, themeHex) => {
    clearRaf();
    clearToneTimer();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    const dur = Math.max(0.7, Number(highlight?.durationSec) || 0.7);
    setMode('tone');
    setHighlightId(highlight?.id || null);
    setIsPlaying(true);
    setCurrentTime(0);
    setDuration(dur);
    playTone(themeHex, Math.min(1200, dur * 1000));

    const started = performance.now();
    const loop = (now) => {
      const elapsed = (now - started) / 1000;
      setCurrentTime(Math.min(dur, elapsed));
      if (elapsed < dur) {
        rafRef.current = requestAnimationFrame(loop);
      } else {
        setIsPlaying(false);
        setCurrentTime(0);
        setHighlightId(null);
        setMode(null);
      }
    };
    rafRef.current = requestAnimationFrame(loop);
  }, []);

  const playClip = useCallback((highlight, { startAt = 0 } = {}) => {
    const localSrc = resolveHighlightAudioSrc(highlight);
    const clipId = highlight?.clipId || highlight?.clip_id;
    const src = localSrc || (clipId ? getClipUrl(clipId) : null);
    const themeHex = themeColor(highlight?.theme);
    if (!src) {
      playToneFallback(highlight, themeHex);
      return;
    }

    clearRaf();
    clearToneTimer();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const audio = new Audio(src);
    audioRef.current = audio;
    setMode('clip');
    setHighlightId(highlight.id);
    setCurrentTime(Math.max(0, startAt));
    setDuration(Number(highlight.durationSec) || 0);
    setIsPlaying(true);

    const applyStart = () => {
      if (startAt > 0 && Number.isFinite(startAt)) {
        try { audio.currentTime = startAt; } catch { /* ignore */ }
        setCurrentTime(startAt);
      }
    };

    audio.onloadedmetadata = () => {
      if (!Number.isNaN(audio.duration) && audio.duration) setDuration(audio.duration);
      applyStart();
    };
    audio.onended = () => {
      clearRaf();
      setIsPlaying(false);
      setCurrentTime(0);
      setHighlightId(null);
      setMode(null);
      audioRef.current = null;
    };
    audio.onerror = () => {
      playToneFallback(highlight, themeHex);
    };
    audio.play()
      .then(() => {
        applyStart();
        clearRaf();
        rafRef.current = requestAnimationFrame(tick);
      })
      .catch(() => playToneFallback(highlight, themeHex));
  }, [playToneFallback, tick]);

  const toggle = useCallback((highlight) => {
    if (!highlight?.id) return;
    if (highlightId === highlight.id && isPlaying) {
      pause();
      return;
    }
    if (highlightId === highlight.id && !isPlaying && audioRef.current) {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          clearRaf();
          rafRef.current = requestAnimationFrame(tick);
        })
        .catch(() => playClip(highlight));
      return;
    }
    playClip(highlight);
  }, [highlightId, isPlaying, pause, playClip, tick]);

  const seek = useCallback((sec) => {
    const audio = audioRef.current;
    if (!audio || mode !== 'clip') return;
    const next = Math.max(0, Math.min(duration || audio.duration || 0, sec));
    audio.currentTime = next;
    setCurrentTime(next);
  }, [duration, mode]);

  useEffect(() => () => stop(), [stop]);

  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

  const value = useMemo(() => ({
    highlightId,
    isPlaying,
    currentTime,
    duration,
    progress,
    mode,
    clock: formatClock(currentTime),
    durationClock: formatClock(duration),
    toggle,
    pause,
    stop,
    seek,
    play: playClip,
    download: downloadHighlight,
    formatClock,
  }), [
    highlightId,
    isPlaying,
    currentTime,
    duration,
    progress,
    mode,
    toggle,
    pause,
    stop,
    seek,
    playClip,
  ]);

  return <PlaybackCtx.Provider value={value}>{children}</PlaybackCtx.Provider>;
}

export function usePlayback() {
  const ctx = useContext(PlaybackCtx);
  if (!ctx) throw new Error('usePlayback must be used within PlaybackProvider');
  return ctx;
}

export function useOptionalPlayback() {
  return useContext(PlaybackCtx);
}
